import React, { useState, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import WineCard from './components/WineCard';
import EventCard from './components/EventCard';
import EventPage from './components/EventPage';
import ContactItem from './components/ContactItem';
import Footer from './components/Footer';
import LoginModal from './components/LoginModal';
import Modal from './components/Modal';
import HeroModal from './components/HeroModal';
import { Instagram, Facebook } from 'lucide-react';
import MailingList from './components/MailingList';
import ImageModal from './components/ImageModal';
import TastingRoom from './components/TastingRoom';
import { moveItem } from './components/SortableImageGrid';
import { getToken, clearToken, login as loginRequest, validateSession } from './adminSession';

const API_BASE_URL = '/api';

function App() {
  // Kept in localStorage so an admin session survives a page refresh.
  const [token, setTokenState] = useState(() => getToken());
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginError, setLoginError] = useState('');

  const isAuthenticated = !!token;
  const [currentView, setCurrentView] = useState('home');
  const [selectedEventId, setSelectedEventId] = useState(null);

  const [wines, setWines] = useState([]);
  const [events, setEvents] = useState([]);
  const [hero, setHero] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showHeroModal, setShowHeroModal] = useState(false);
  const [contentType, setContentType] = useState('wine');
  const [formData, setFormData] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedEventImages, setSelectedEventImages] = useState([]);

    // Image modal state
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState('');
  const [modalImageAlt, setModalImageAlt] = useState('')

  useEffect(() => { loadData(); }, []);

  // A deep link to an event that does not exist (or lost its page) falls back
  // to the home page once loading has finished.
  useEffect(() => {
    if (loading) return;
    if (currentView !== 'event-page' || !selectedEventId) return;

    const target = events.find(e => e.id === selectedEventId);
    if (!target || !target.hasPage) {
      setCurrentView('home');
      setSelectedEventId(null);
      window.history.replaceState({}, '', '/');
    }
  }, [loading, events, currentView, selectedEventId]);

  // A stored token may have expired or been signed with an old secret, so
  // confirm it with the server rather than trusting what is in localStorage.
  useEffect(() => {
    validateSession().then((valid) => {
      if (!valid) setTokenState(null);
    });
  }, []);

  useEffect(() => {
    const applyLocation = () => {
      const path = window.location.pathname.match(/^\/events\/(\d+)/);
      if (path) {
        setCurrentView('event-page');
        setSelectedEventId(parseInt(path[1], 10));
        return;
      }

      // Old #event/123 links stay working: rewrite them to the real URL.
      const hash = window.location.hash;
      if (hash.startsWith('#event/')) {
        const eventId = parseInt(hash.replace('#event/', ''), 10);
        window.history.replaceState({}, '', `/events/${eventId}`);
        setCurrentView('event-page');
        setSelectedEventId(eventId);
        return;
      }

      setCurrentView('home');
      setSelectedEventId(null);
    };

    window.addEventListener('popstate', applyLocation);
    applyLocation();

    return () => window.removeEventListener('popstate', applyLocation);
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [winesRes, eventsRes, heroRes] = await Promise.all([
        fetch(`${API_BASE_URL}/wines`),
        fetch(`${API_BASE_URL}/events`),
        fetch(`${API_BASE_URL}/hero`)
      ]);
      
      if (!winesRes.ok || !eventsRes.ok || !heroRes.ok) throw new Error('Failed to load data');
      
      setWines(await winesRes.json());
      setEvents(await eventsRes.json());
      setHero(await heroRes.json());
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

    // Image modal handlers
  const handleImageClick = (imageUrl, altText) => {
    setModalImageUrl(imageUrl);
    setModalImageAlt(altText);
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setModalImageUrl('');
    setModalImageAlt('');
  };

  const handleLogin = async (username, password) => {
    const result = await loginRequest(username, password);
    if (result.ok) {
      setTokenState(result.token);
      setShowLoginModal(false);
      setLoginError('');
    } else {
      setLoginError(result.message);
    }
  };

  const handleLogout = () => {
    clearToken();
    setTokenState(null);
  };

  // Every admin request goes through here so the token is attached in one place
  // and an expired session drops straight back to the login prompt.
  const authFetch = async (url, options = {}) => {
    const headers = { ...options.headers };
    const stored = getToken();
    if (stored) headers.Authorization = `Bearer ${stored}`;

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      handleLogout();
      setShowLoginModal(true);
      setLoginError('Your session expired. Please log in again.');
      throw new Error('Session expired');
    }
    return response;
  };

  const handleAddContentClick = () => {
      setEditMode(false);
      setEditingId(null);
      setFormData({});
      setSelectedImage(null);
      setSelectedEventImages([]);
      setShowModal(true);
  };

  const handleViewEventPage = (eventId) => {
    setCurrentView('event-page');
    setSelectedEventId(eventId);
    window.history.pushState({}, '', `/events/${eventId}`);
    window.scrollTo(0, 0);
  };

  const handleBackToHome = () => {
    setCurrentView('home');
    setSelectedEventId(null);
    window.history.pushState({}, '', '/');
  };

  const handleEditWine = (wine) => {
    setEditMode(true);
    setEditingId(wine.id);
    setContentType('wine');
    setFormData({
      wineName: wine.name,
      wineType: wine.type,
      wineDescription: wine.description,
      winePrice: wine.price,
      wineEmoji: wine.emoji,
      wineImage: wine.image || ''
    });
    setSelectedImage(null);
    setSelectedEventImages([]);
    setShowModal(true);
  };

  const handleEditEvent = (event) => {
    setEditMode(true);
    setEditingId(event.id);
    setContentType('event');
    setFormData({
      eventTitle: event.title,
      eventDate: event.date,
      eventDescription: event.description,
      eventEmoji: event.emoji,
      eventImage: event.image || '',
      hasPage: event.hasPage || false,
      pageTitle: event.pageTitle || '',
      pageDescription: event.pageDescription || '',
      pageImages: event.pageImages || []
    });
    setSelectedImage(null);
    setSelectedEventImages([]);
    setShowModal(true);
  };

  const handleDeleteWine = async (id) => {
    if (!window.confirm('Are you sure you want to delete this wine?')) return;
    try {
      const response = await authFetch(`${API_BASE_URL}/wines/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete wine');
      setWines(wines.filter(wine => wine.id !== id));
    } catch (error) {
      console.error('Error deleting wine:', error);
      alert('Failed to delete wine. Please try again.');
    }
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      const response = await authFetch(`${API_BASE_URL}/events/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete event');
      setEvents(events.filter(event => event.id !== id));
      if (selectedEventId === id) {
        handleBackToHome();
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event. Please try again.');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, imagePreview: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEventImagesSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setSelectedEventImages(files);
    
    const previews = [];
    let loadedCount = 0;
    
    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Readers finish in any order; place each preview at its file's position.
        previews[index] = reader.result;
        loadedCount++;
        if (loadedCount === files.length) {
          setFormData({ ...formData, eventImagePreviews: previews });
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async () => {
    if (!selectedImage) return null;
    setUploadingImage(true);
    try {
      const formDataImg = new FormData();
      formDataImg.append('image', selectedImage);
      const endpoint = contentType === 'event' ? 'event-card-image' : 'wine-image';
      const response = await authFetch(`${API_BASE_URL}/upload/${endpoint}`, {
        method: 'POST',
        body: formDataImg,
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to upload image');
      }
      
      const data = await response.json();
      return data.imageUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      alert(`Failed to upload image: ${error.message}`);
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleEventImagesUpload = async () => {
    if (!selectedEventImages || selectedEventImages.length === 0) {
      return [];
    }
    
    setUploadingImage(true);
    try {
      console.log('Uploading', selectedEventImages.length, 'event images...');

      // The server accepts at most 20 files per request, so send them in batches.
      const BATCH_SIZE = 20;
      const imageUrls = [];
      for (let i = 0; i < selectedEventImages.length; i += BATCH_SIZE) {
        const formDataImg = new FormData();
        selectedEventImages.slice(i, i + BATCH_SIZE).forEach(file => {
          formDataImg.append('images', file);
        });

        const response = await authFetch(`${API_BASE_URL}/upload/event-images`, {
          method: 'POST',
          body: formDataImg,
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to upload images');
        }

        const data = await response.json();
        imageUrls.push(...data.imageUrls);
      }

      console.log('Images uploaded successfully:', imageUrls);
      return imageUrls;
    } catch (error) {
      console.error('Error uploading images:', error);
      alert(`Failed to upload images: ${error.message}`);
      return [];
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAddContent = async () => {
    try {
      let imageUrl = '';
      
      if (contentType === 'wine') {
        imageUrl = formData.wineImage || '';
        if (selectedImage) {
          const uploadedUrl = await handleImageUpload();
          if (uploadedUrl) imageUrl = uploadedUrl;
        }
      }

      if (editMode) {
        if (contentType === 'wine') {
          const updatedWine = {
            name: formData.wineName || '',
            type: formData.wineType || '',
            description: formData.wineDescription || '',
            price: formData.winePrice || '',
            emoji: formData.wineEmoji || '🍷',
            image: imageUrl
          };
          const response = await authFetch(`${API_BASE_URL}/wines/${editingId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updatedWine),
          });
          if (!response.ok) throw new Error('Failed to update wine');
          const savedWine = await response.json();
          setWines(wines.map(wine => wine.id === editingId ? savedWine : wine));
        } else if (contentType === 'event') {
          let eventImageUrl = formData.eventImage || '';
          if (selectedImage) {
            const uploadedUrl = await handleImageUpload();
            if (uploadedUrl) eventImageUrl = uploadedUrl;
          }

          let pageImages = formData.pageImages || [];
          
          if (selectedEventImages.length > 0) {
            const uploadedUrls = await handleEventImagesUpload();
            if (uploadedUrls.length > 0) {
              pageImages = [...pageImages, ...uploadedUrls];
            }
          }

          const updatedEvent = {
            title: formData.eventTitle || '',
            date: formData.eventDate || '',
            description: formData.eventDescription || '',
            emoji: formData.eventEmoji || '🎉',
            image: eventImageUrl,
            hasPage: formData.hasPage || false,
            pageTitle: formData.pageTitle || '',
            pageDescription: formData.pageDescription || '',
            pageImages: pageImages
          };
          const response = await authFetch(`${API_BASE_URL}/events/${editingId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updatedEvent),
          });
          if (!response.ok) throw new Error('Failed to update event');
          const savedEvent = await response.json();
          setEvents(events.map(event => event.id === editingId ? savedEvent : event));
        }
      } else {
        if (contentType === 'wine') {
          const newWine = {
            name: formData.wineName || '',
            type: formData.wineType || '',
            description: formData.wineDescription || '',
            price: formData.winePrice || '',
            emoji: formData.wineEmoji || '🍷',
            image: imageUrl
          };
          const response = await authFetch(`${API_BASE_URL}/wines`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(newWine),
          });
          if (!response.ok) throw new Error('Failed to create wine');
          const savedWine = await response.json();
          setWines([...wines, savedWine]);
        } else if (contentType === 'event') {
          let eventImageUrl = '';
          if (selectedImage) {
            // A failed upload returns null; storing that would put a broken
            // value in the record, so fall back to no image.
            eventImageUrl = (await handleImageUpload()) || '';
          }

          let pageImages = [];
          
          if (formData.hasPage && selectedEventImages.length > 0) {
            pageImages = await handleEventImagesUpload();
          }

          const newEvent = {
            title: formData.eventTitle || '',
            date: formData.eventDate || '',
            description: formData.eventDescription || '',
            emoji: formData.eventEmoji || '🎉',
            image: eventImageUrl,
            hasPage: formData.hasPage || false,
            pageTitle: formData.pageTitle || '',
            pageDescription: formData.pageDescription || '',
            pageImages: pageImages
          };
          const response = await authFetch(`${API_BASE_URL}/events`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(newEvent),
          });
          if (!response.ok) throw new Error('Failed to create event');
          const savedEvent = await response.json();
          setEvents([savedEvent, ...events]);
        }
      }

      setShowModal(false);
      setFormData({});
      setEditMode(false);
      setEditingId(null);
      setSelectedImage(null);
      setSelectedEventImages([]);
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  const handleRemoveEventImage = (index) => {
    const updatedImages = [...(formData.pageImages || [])];
    updatedImages.splice(index, 1);
    setFormData({ ...formData, pageImages: updatedImages });
  };

  const handleMovePageImage = (from, to) => {
    setFormData({ ...formData, pageImages: moveItem(formData.pageImages || [], from, to) });
  };

  // New images keep their files and previews side by side, so both move together.
  const handleMoveNewEventImage = (from, to) => {
    setSelectedEventImages(moveItem(selectedEventImages, from, to));
    setFormData({ ...formData, eventImagePreviews: moveItem(formData.eventImagePreviews || [], from, to) });
  };

  const handleRemoveNewEventImage = (index) => {
    setSelectedEventImages(selectedEventImages.filter((_, i) => i !== index));
    setFormData({ ...formData, eventImagePreviews: (formData.eventImagePreviews || []).filter((_, i) => i !== index) });
  };

  const handleEditHero = () => {
    setFormData({
      title: hero.title || '',
      subtitle: hero.subtitle || '',
      buttonText: hero.buttonText || '',
      backgroundImages: hero.backgroundImages || []
    });
    setSelectedImage(null);
    setSelectedEventImages([]);
    setShowHeroModal(true);
  };

  const handleHeroImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setSelectedEventImages(files); // Reuse existing state
    
    const previews = [];
    let loadedCount = 0;
    
    files.forEach((file, index) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Readers finish in any order; place each preview at its file's position.
        previews[index] = reader.result;
        loadedCount++;
        if (loadedCount === files.length) {
          setFormData({ ...formData, imagePreviews: previews });
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSaveHero = async () => {
    try {
      let backgroundImages = [...(formData.backgroundImages || [])];
      
      if (selectedEventImages.length > 0) {
        setUploadingImage(true);
        const formDataImg = new FormData();
        selectedEventImages.forEach(file => {
          formDataImg.append('images', file);
        });
        
        const response = await authFetch(`${API_BASE_URL}/upload/hero-images`, {
          method: 'POST',
          body: formDataImg,
        });
        
        if (response.ok) {
          const data = await response.json();
          backgroundImages = [...backgroundImages, ...data.imageUrls];
        }
        setUploadingImage(false);
      }

      const updatedHero = {
        title: formData.title || '',
        subtitle: formData.subtitle || '',
        buttonText: formData.buttonText || '',
        backgroundImages: backgroundImages
      };

      const response = await authFetch(`${API_BASE_URL}/hero`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedHero),
      });

      if (!response.ok) throw new Error('Failed to update hero');

      const savedHero = await response.json();
      setHero(savedHero);
      setShowHeroModal(false);
      setFormData({});
      setSelectedImage(null);
      setSelectedEventImages([]);
    } catch (error) {
      console.error('Error saving hero:', error);
      alert('Failed to save hero changes. Please try again.');
    }
  };

  const handleRemoveHeroImage = (index) => {
    const updatedImages = [...(formData.backgroundImages || [])];
    updatedImages.splice(index, 1);
    setFormData({ ...formData, backgroundImages: updatedImages });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#722f37' }}>
        <div style={{ color: 'white', fontSize: '1.5rem' }}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#722f37' }}>
        <div style={{ color: 'white', textAlign: 'center', maxWidth: '32rem', padding: '1rem' }}>
          <h2 style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '1rem' }}>⚠️ Connection Error</h2>
          <p style={{ marginBottom: '1rem' }}>{error}</p>
          <button onClick={loadData} style={{ backgroundColor: '#f4bc57', color: '#722f37', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', fontWeight: 'bold', border: 'none', cursor: 'pointer' }}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const selectedEvent = currentView === 'event-page' && selectedEventId
    ? events.find(e => e.id === selectedEventId)
    : null;
  if (currentView === 'event-page' && selectedEventId) {
    const event = selectedEvent;
    if (event && event.hasPage) {
      return (
        <>
          <EventPage 
            event={event} 
            onBack={handleBackToHome} 
            isAuthenticated={isAuthenticated} 
            onEdit={handleEditEvent} 
            onDelete={handleDeleteEvent}
            onImageClick={handleImageClick}
          />
          {showImageModal && (
            <ImageModal 
              imageUrl={modalImageUrl} 
              altText={modalImageAlt} 
              onClose={handleCloseImageModal} 
            />
          )}
        </>
      );
    }
    // Falling through to the home page happens in an effect, never in render.
    return null;
  }

  const categories = [
    { label: 'Dry Whites', types: ['Dry White'] },
    { label: 'Dry Reds', types: ['Dry Red', 'Dry Red - Barrique Method'] },
    { label: 'Semi-Sweets', types: ['Semi-Sweet'] },
    { label: 'Dessert', types: ['Dessert'] }
  ];
  
const winesByCategory = {};

// First, handle predefined categories
categories.forEach(cat => {
  const categoryWines = wines.filter(wine =>
    cat.types.some(type =>
      wine.type && wine.type.toLowerCase() === type.toLowerCase()
    )
  );
  if (categoryWines.length > 0) {
    winesByCategory[cat.label] = categoryWines;
  }
});

// Collect types already defined
const definedTypes = categories.flatMap(c => c.types.map(t => t.toLowerCase()));

// Handle "other" types
const otherWines = wines.filter(
  wine => wine.type && !definedTypes.includes(wine.type.toLowerCase())
);

// Group other wines by type
otherWines.forEach(wine => {
  const typeLabel = wine.type; // Use the type as the category label
  if (!winesByCategory[typeLabel]) {
    winesByCategory[typeLabel] = [];
  }
  winesByCategory[typeLabel].push(wine);
});

  return (
    <div className="min-h-screen bg-white">
      <Header isAuthenticated={isAuthenticated} onLogout={handleLogout} onLoginClick={() => setShowLoginModal(true)} />
      <HeroSection hero={hero} isAuthenticated={isAuthenticated} onEdit={handleEditHero} />

      <section id="about" className="pt-20 pb-10 px-4 bg-beige">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl font-bold mb-4 text-wine relative pb-6 section-divider">Our Story</h2>
          <div className="space-y-6 mt-12 text-lg text-gray-600">
            <p>Vivat Alfa Winery is a family-owned and operated boutique winery located in a historic pre-Revolutionary War barn in the heart of Bucks County, PA. Founded in 2013, we specialize in handcrafting artisan wines that exhibit pure and true varietal character.</p>
            <p>Our European-style, estate wines are an expression of our passion and commitment to quality. We follow a simple philosophy: nothing added, nothing subtracted. This dedication to authenticity allows the true character of each grape variety to shine through.</p>
            <p>Visit us on weekends to experience our wines in our rustic tasting room with an outdoor patio overlooking our vineyards. Taste our European tradition and discover why we're one of the few wineries in the region growing rare varieties like Lemberger.</p>
          </div>
        </div>
      </section>

      <TastingRoom />

      <section id="wines" className="py-10 px-4 bg-beige">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-4 text-wine relative pb-6 section-divider">Our Premium Wines</h2>
          
          {Object.entries(winesByCategory).map(([categoryLabel, categoryWines]) => (
            <div key={categoryLabel} className="mb-16 mt-12">
              <h3 className="text-3xl font-bold text-wine mb-8 text-center">{categoryLabel}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                {categoryWines.map(wine => (
                  <WineCard key={wine.id} wine={wine} isAuthenticated={isAuthenticated} onDelete={handleDeleteWine} onEdit={handleEditWine} onImageClick={handleImageClick}/>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="events" className="py-10 px-4 bg-beige">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-4 text-wine relative pb-6 section-divider">Events & News</h2>
          <div className="space-y-10 mt-12">
            {events.map(event => <EventCard key={event.id} event={event} isAuthenticated={isAuthenticated} onDelete={handleDeleteEvent} onEdit={handleEditEvent} onViewPage={handleViewEventPage} onImageClick={handleImageClick} />)}
          </div>
        </div>
      </section>



       <section id="mailing" className="py-10 px-4 bg-beige">
        <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-5xl font-bold mb-4 text-wine relative pb-6 section-divider">Join Our Mailing List</h2>
            <MailingList />
            </div>
      </section>

      <section id="contact" className="py-10 px-4 bg-beige">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-4 text-wine relative pb-6 section-divider">Visit Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mt-12">
            <ContactItem icon="📍" title="Location" info={['3612 Stump Road', 'Doylestown, PA 18902']} />
            <ContactItem icon="📞" title="Phone" info={['(267) 614-5011']} />
<ContactItem 
  icon="✉️" 
  title="Social Media" 
  info={[
    <a 
      key="ig"
      href="https://instagram.com/vivatalfa_winery"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 text-wine underline"
    >
      <Instagram /> @VivatAlfa_Winery
    </a>,
    <a 
      key="fb"
      href="https://www.facebook.com/profile.php?id=61570319830153"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 text-wine underline"
    >
      <Facebook /> Vivat Alfa on Facebook
    </a>
  ]}
/>
            <ContactItem icon="🕐" title="Hours" info={['Sat-Sun: 12pm - 6pm', 'Mon-Fri: Closed']} />
          </div>
        </div>
      </section>

      <Footer />

      {/* No login control on the public site — admins go to /admin. This only
          renders once signed in. */}
      {isAuthenticated && (
        <button
          onClick={handleLogout}
          aria-label="Log out of admin"
          className="fixed bottom-8 left-8 bg-wine hover:bg-wine-light text-white px-3 py-2 rounded-lg shadow-lg transition-all hover:scale-105 text-sm font-bold flex items-center gap-2"
          style={{ zIndex: 50 }}
        >
          🔓 Logout
        </button>
      )}

      {isAuthenticated && (
        <button onClick={handleAddContentClick} aria-label="Add a wine or event" className="fixed bottom-8 right-8 w-16 h-16 bg-wine hover:bg-wine-light text-white rounded-full text-4xl flex items-center justify-center shadow-lg transition-all hover:scale-110" style={{ zIndex: 50 }}>+</button>
      )}
       {showImageModal && (
        <ImageModal 
          imageUrl={modalImageUrl} 
          altText={modalImageAlt} 
          onClose={handleCloseImageModal} 
        />
      )}

      {showLoginModal && <LoginModal onLogin={handleLogin} onClose={() => { setShowLoginModal(false); setLoginError(''); }} error={loginError} />}
      {showModal && <Modal contentType={contentType} setContentType={setContentType} formData={formData} onInputChange={handleInputChange} onImageSelect={handleImageSelect} onEventImagesSelect={handleEventImagesSelect} uploadingImage={uploadingImage} onClose={() => { setShowModal(false); setFormData({}); setEditMode(false); setEditingId(null); setSelectedImage(null); setSelectedEventImages([]); }} onSubmit={handleAddContent} editMode={editMode} onRemoveEventImage={handleRemoveEventImage} onMovePageImage={handleMovePageImage} onMoveNewEventImage={handleMoveNewEventImage} onRemoveNewEventImage={handleRemoveNewEventImage} />}
      {showHeroModal && <HeroModal hero={formData} onInputChange={handleInputChange} onImageSelect={handleHeroImageSelect} uploadingImage={uploadingImage} onClose={() => { setShowHeroModal(false); setFormData({}); setSelectedImage(null); setSelectedEventImages([]); }} onSubmit={handleSaveHero} onRemoveImage={handleRemoveHeroImage} />}
    </div>
  );
}

export default App;
